import React, { useEffect, useMemo, useState, useRef } from "react";
import useCreatorMovieData from "./CreatorMovieData";
import { deleteVideo, getLibraryByCategory } from "../../api/HttpApis";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../utils/Toastify/Toastify";
import ListView from "../../component/ListView/ListView";
import { qcBadge, contentStats } from "../../component/ListView/contentCard";
import { trimStr } from "../../utils/theme/ShortenedText/ShortenedText";
import { useSearch } from "../../context/SearchContext";
import { debounce } from "../../utils/debounce";
import FloatingButton from "../../component/FloatingButton/FloatingButton";
import RejectionModal from "../../component/ReviewFeedback/RejectionModal";

const CreatorMovie = () => {
  const [movieData, setMovieData] = useState([]);
  const { MovieData } = useCreatorMovieData();
  const [deletingId, setDeletingId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedCreator, setSelectedCreator] = useState("");
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedGenre, setSelectedGenre] = useState();
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { searchQuery } = useSearch();
  const prevSearchQuery = useRef(searchQuery);

  const navigate = useNavigate();

  const fetchData = async (page, limit) => {
    if (searchQuery) return;
    setLoading(true);
    try {
      const data = await MovieData(
        selectedDate,
        selectedCreator,
        selectedRating,
        selectedGenre,
        page,
        limit
      );
      setMovieData(data);
      // Same fix as Movie.jsx: pull totalCount from the response on first
      // load so the Total stat tile and pager populate without waiting on
      // handleFilterChange.
      if (Array.isArray(data) && data.length > 0) {
        setTotalCount(data[0].totalCount || 0);
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to fetch movies",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Opening a rejected item surfaces the reviewer's feedback first — same
  // behaviour as the main Content page.
  const [rejectionInfo, setRejectionInfo] = useState(null);

  const handleEdit = (id, metadata_id, item) => {
    const isRejected =
      String(item?.qc_status || "").trim().toLowerCase() === "rejected";
    if (isRejected) {
      setRejectionInfo({
        name: item?.name || "",
        reason: item?.qc_reason || null,
        note: item?.qc_note || null,
        id,
        metadata_id,
      });
      return;
    }
    openEditor(id, metadata_id);
  };

  const openEditor = (id, metadata_id) => {
    const urlId = metadata_id || id;
    const hasMetadataId = Boolean(metadata_id);
    const url = hasMetadataId ? `/video/${urlId}` : `/video/${urlId}?meta=false`;

    navigate(url, {
      state: {
        name: "creator-movie",
        movieId: id,
        metadata_id: metadata_id || "",
      },
    });
  };

  const handleDelete = async (id, metadata_id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (!confirmed) return;
    const deleteId = metadata_id || id;
    setDeletingId(deleteId);
    try {
      const response = await deleteVideo(deleteId);
      setMovieData((prev) =>
        prev.filter((item) => (item.metadata_id || item.id) !== deleteId)
      );
      // Keep the Total tile in step without a refetch.
      setTotalCount((c) => Math.max(0, (Number(c) || 0) - 1));
      showToast(response.data.message, "success");
    } catch (error) {
      showToast(error.response.data.message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  const cardFor = (item) => ({
    id: item.metadata_id || item.id,
    title: item.name,
    image: item.media,
    meta: [item.type, item.access].filter((v) => v && v !== "N/A").join(" · "),
    badge: qcBadge(item),
    actions: [
      {
        icon: "edit",
        label: "Edit",
        onClick: () => handleEdit(item.id, item.metadata_id, item),
      },
      {
        icon: "delete",
        label: "Delete",
        disabled: deletingId === (item.metadata_id || item.id),
        onClick: () => handleDelete(item.id, item.metadata_id),
      },
    ],
  });

  const stats = contentStats(movieData, totalCount);

  const handleFilterChange = async (type, value) => {
    if (type === "genre") setSelectedGenre(value);
    if (type === "date") setSelectedDate(value);
    if (type === "creator") setSelectedCreator(value);
    if (type === "rating") setSelectedRating(value);

    const updatedGenre = type === "genre" ? value : selectedGenre;
    const updatedDate = type === "date" ? value : selectedDate;
    const updatedCreator = type === "creator" ? value : selectedCreator;
    const updatedRating = type === "rating" ? value : selectedRating;

    setLoading(true);
    try {
      const response = await getLibraryByCategory(
        "Movie",
        {},
        updatedDate,
        updatedCreator,
        updatedRating,
        updatedGenre,
        "",
        "",
        searchQuery
      );

      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      const formattedData = data.map((item) => ({
        id: item.id,
        metadata_id: item.metadata_id,
        name: trimStr(item.name),
        media: item.portrait,
        status: item.status,
        qc_status: item.qc_status,
        qc_reason: item.qc_reason,
        qc_note: item.qc_note,
        type: item.type,
        access: item.access,
        submissionDate:
          new Date(item.dateUploaded).toLocaleDateString("en-US") || "N/A",
        buttonText: "Edit",
        buttonText2: "Delete",
      }));

      setMovieData(formattedData);
      setTotalCount(response?.data?.total);
      showToast();
    } catch (error) {
      showToast("Failed to apply filter", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip the debounced search-fetch on mount / when searchQuery hasn't
    // actually changed — same fix applied across Movie/Podcasts/etc.
    if (prevSearchQuery.current === searchQuery) {
      return;
    }
    prevSearchQuery.current = searchQuery;

    const timer = setTimeout(() => {
      handleFilterChange("search", searchQuery || "");
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <div>
      <div>
        <ListView
          heading="Movies"
          data={movieData}
          getCard={cardFor}
          stats={stats}
          onPageChange={fetchData}
          button={true}
          handleFilterChange={handleFilterChange}
          totalCount={totalCount}
          selectedDate={selectedDate}
          selectedRating={selectedRating}
          selectedGenre={selectedGenre}
          tableLoading={loading}
          addLabel="Add Movies"
          onAdd={() => navigate("/video", { state: { name: "creator-movie" } })}
        />
      </div>
      <FloatingButton />

      {/* Rejection feedback for the item the creator just opened. */}
      <RejectionModal
        open={!!rejectionInfo}
        contentName={rejectionInfo?.name}
        reason={rejectionInfo?.reason}
        note={rejectionInfo?.note}
        onClose={() => setRejectionInfo(null)}
        onEdit={() => {
          const info = rejectionInfo;
          setRejectionInfo(null);
          if (info) openEditor(info.id, info.metadata_id);
        }}
      />
    </div>
  );
};

export default CreatorMovie;